import {
    Box, CircularProgress, Grid, IconButton, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { NumericFormat } from 'react-number-format';
import { CurrencyInput } from '@components/CurrencyInput';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { SalesGoalInput } from '../services/requests/save-sales-goals.request';
import { SalesGoalRow } from '../services/responses/sales-goals.response';

interface Props {
    users: SalesGoalRow[];
    drafts: SalesGoalInput[];
    loading: boolean;
    emptyMessage: string;
    page: number;
    rowsPerPage: number;
    onChange: (userId: string, patch: Partial<SalesGoalInput>) => void;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
    savingUserId?: string;
    onSave: (userId: string) => void;
    onRemove: (userId: string) => void;
}

export const SalesGoalsTable = ({
    users, drafts, loading, emptyMessage, page, rowsPerPage, onChange, onPageChange, onRowsPerPageChange,
    savingUserId, onSave, onRemove,
}: Props) => {
    const { t } = useTranslation();
    const { alertDelete } = useAlert();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const inputSx = {
        '& .MuiOutlinedInput-root': {
            bgcolor: cc.surfaceBg,
            borderRadius: '8px',
            color: cc.textPrimary,
            '& fieldset': { borderColor: cc.border },
        },
        '& .MuiInputBase-input': { color: cc.textPrimary },
    };
    const visibleUsers = users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2 }}>
            <TableContainer sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <Table size="medium">
                    <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'background.card' }}>
                        <TableRow>
                            <TableCell width="70%">{t('general.nome')}</TableCell>
                            <TableCell width="15%">{t('metas.metaFaturamento')}</TableCell>
                            <TableCell width="20%">{t('metas.metaVendas')}</TableCell>
                            <TableCell width="10%" sx={{ minWidth: 10 }}>{t('general.acoes')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? Array.from({ length: 5 }).map((_, index) => (
                            <TableRow key={index}>
                                <TableCell><Skeleton height={28} /></TableCell>
                                <TableCell><Skeleton height={40} /></TableCell>
                                <TableCell><Skeleton height={40} /></TableCell>
                                <TableCell><Skeleton height={28} /></TableCell>
                            </TableRow>
                        )) : users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <Typography sx={{ color: cc.textSecondary }}>{emptyMessage}</Typography>
                                </TableCell>
                            </TableRow>
                        ) : visibleUsers.map((user) => {
                            const draft = drafts.find((row) => row.userId === user.userId);
                            const isSaved = user.revenueTarget != null || user.salesTarget != null;
                            return (
                                <TableRow key={user.userId} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, bgcolor: user.color }} />
                                            <Typography sx={{ color: cc.textPrimary, fontSize: '0.875rem' }}>{user.name}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ maxWidth: 240 }}>
                                            <CurrencyInput
                                                fullWidth
                                                value={draft?.revenueTarget ?? null}
                                                onChange={(value) => onChange(user.userId, { revenueTarget: value })}
                                                sx={inputSx}
                                            />
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ maxWidth: 240 }}>
                                            <NumericFormat
                                                value={draft?.salesTarget ?? ''}
                                                onValueChange={(values) => onChange(user.userId, { salesTarget: values.floatValue ?? null })}
                                                decimalScale={0}
                                                allowNegative={false}
                                                thousandSeparator="."
                                                decimalSeparator=","
                                                customInput={TextField}
                                                size="small"
                                                fullWidth
                                                sx={inputSx}
                                            />
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 90 }}>
                                        <Grid container alignItems="center" spacing={1} wrap="nowrap">
                                            <Tooltip title={t('general.salvar')}>
                                                <span>
                                                    <IconButton size="small" color="success" aria-label={t('general.salvar')}
                                                        onClick={() => onSave(user.userId)} disabled={Boolean(savingUserId)}>
                                                        {savingUserId === user.userId ? <CircularProgress size={18} /> : <CheckIcon fontSize="small" />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            {isSaved && (
                                                <Tooltip title={t('metas.apagar')}>
                                                    <span>
                                                        <IconButton size="small" color="error" aria-label={t('metas.apagar')}
                                                            onClick={() => alertDelete(user.userId, onRemove, t('metas.metaDe', { nome: user.name }))} disabled={Boolean(savingUserId)}>
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </Grid>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage={t('general.linhasPorPagina')}
                labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} ${t('general.X-de-X')} ${count !== -1 ? count : `${t('general.maisQue')} ${to}`}`
                }
                component="div"
                count={users.length}
                page={page}
                onPageChange={(_, nextPage) => onPageChange(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => onRowsPerPageChange(Number.parseInt(event.target.value, 10))}
            />
        </Paper>
    );
};
